import { apiFetch } from './client.ts';

/**
 * MVP-278. Self-serve plans: the public sign-up, the shop paying for a plan with a PromptPay slip,
 * and the admin deciding the slip. The contract is `strapi-template/src/api/plan/` and
 * `strapi-template/src/api/payment/` (commit 28372fc); who may call what is
 * `PAYMENT_SHOP_ACTIONS` / `PAYMENT_ADMIN_ACTIONS` in `strapi-template/src/bootstrap/roles.js`.
 */

export interface Plan {
  code: string;
  nameTh: string;
  days: number;
  maxVenues: number;
  maxRoomsPerVenue: number;
  /** Null while the owner has not priced the plan — the server refuses a payment for it. */
  priceThb: number | null;
  trialOnly: boolean;
}

export type PaymentStatus = 'pending' | 'approved' | 'rejected';

export interface Payment {
  documentId: string;
  plan: { code: string; nameTh: string; days: number } | null;
  amountThb: number;
  method: string;
  paymentStatus: PaymentStatus;
  /** MVP-278 harden: the slip's type, or null with no slip. Never a URL — the slip is private;
   * `slipLink` asks for a five-minute one. */
  slipMime: string | null;
  note: string | null;
  decidedAt: string | null;
  createdAt: string | null;
  /** Admin only. */
  licence?: { slug: string; customerName: string | null; expiresAt: string | null } | null;
  /** Admin only: the deciding admin's username. */
  decidedBy?: string | null;
}

export interface SignupInput {
  shopName: string;
  email: string;
  password: string;
  phone?: string;
  turnstileToken?: string;
}

/** PUBLIC. Creates the account, its licence on the trial plan, one venue and one room, and mails a
 * confirmation link. The account cannot sign in until that link is opened (`confirmSignup`). */
export async function signup(input: SignupInput): Promise<{ username: string; email: string }> {
  const res = await apiFetch<{ data: { username: string; email: string } }>('/api/signup', {
    method: 'POST',
    body: { ...input },
    auth: false,
    retry: false,
  });
  return res.data;
}

/** PUBLIC. The link in the confirmation mail: confirms the account and starts the trial. No JWT —
 * the person signs in with their password afterwards. */
export async function confirmSignup(token: string): Promise<{ username: string }> {
  return (
    await apiFetch<{ data: { username: string } }>('/api/signup/confirm', { method: 'POST', body: { token }, auth: false, retry: false })
  ).data;
}

/** PUBLIC. A new confirmation link. The server answers 200 whatever the address, so this says nothing. */
export async function resendSignup(email: string): Promise<void> {
  await apiFetch('/api/signup/resend', { method: 'POST', body: { email }, auth: false, retry: false });
}

/** PUBLIC. Whether the server takes sign-ups at all (`SELF_SERVE_SIGNUP_ENABLED`). */
export async function signupOpen(): Promise<boolean> {
  return (await apiFetch<{ data: { open: boolean } }>('/api/signup', { auth: false })).data.open === true;
}

/** The admin, or the shop that sent it: a presigned link to the private slip, dead in ~5 minutes. */
export async function slipLink(documentId: string): Promise<string> {
  return (await apiFetch<{ data: { url: string } }>(`/api/payments/${encodeURIComponent(documentId)}/slip`)).data.url;
}

/** The one account every shop pays into, set by the admin in Settings. `bank` only when all three
 * fields are set; either may be null. */
export interface PayTo {
  bank: { name: string; accountName: string; accountNo: string } | null;
  promptPayId: string | null;
}

export interface PaymentAccountInput {
  bankName: string;
  bankAccountName: string;
  bankAccountNo: string;
  promptPayId: string;
}

/** The plans on sale, and where to pay for them (`meta.payTo`). */
export async function listPlans(): Promise<{ plans: Plan[]; payTo: PayTo }> {
  const res = await apiFetch<{ data: Plan[]; meta?: { payTo?: PayTo } }>('/api/plans');
  return { plans: res.data, payTo: res.meta?.payTo ?? { bank: null, promptPayId: null } };
}

/** Admin. Empty fields clear; the answer is the new `payTo`. */
export async function savePaymentAccount(input: PaymentAccountInput): Promise<PayTo> {
  return (await apiFetch<{ data: PayTo }>('/api/plans/payment-account', { method: 'PUT', body: { ...input }, retry: false })).data;
}

/** A shop gets its own licence's payments; an admin every payment. Newest first. */
export async function listPayments(status?: PaymentStatus): Promise<Payment[]> {
  const query = status ? `?status=${status}` : '';
  return (await apiFetch<{ data: Payment[] }>(`/api/payments${query}`)).data;
}

/** Shop. multipart: the plan's code and the transfer slip (an image or a PDF). */
export async function createPayment(planCode: string, slip: File): Promise<Payment> {
  const form = new FormData();
  form.append('planCode', planCode);
  form.append('slip', slip);
  return (await apiFetch<{ data: Payment }>('/api/payments', { method: 'POST', body: form, retry: false })).data;
}

/** Admin. Approving applies the plan to the shop's licence. */
export async function approvePayment(documentId: string, note?: string): Promise<void> {
  await apiFetch(`/api/payments/${encodeURIComponent(documentId)}/approve`, {
    method: 'POST',
    body: note ? { note } : {},
    retry: false,
  });
}

/** Admin. The note is required — the shop is shown it. */
export async function rejectPayment(documentId: string, note: string): Promise<void> {
  await apiFetch(`/api/payments/${encodeURIComponent(documentId)}/reject`, {
    method: 'POST',
    body: { note },
    retry: false,
  });
}
